/**
 * Review Queue — the manager's gate.
 *
 * Submitted work stops here. Nothing an assignee submits reaches a client
 * until it is approved, which is the point of separating "submit" from
 * "deliver" in the work item state machine.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ReviewItem } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Check, X, RotateCcw, Paperclip } from 'lucide-react';

export function ReviewQueuePage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [comments, setComments] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'review-queue'],
    queryFn: () => api.getReviewQueue(),
  });

  const decide = useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome: string }) =>
      api.reviewWorkItem(id, { outcome, comment: comments[id] || null }),
    onSuccess: (r, vars) => {
      if (!r.success) {
        toast({ title: 'Could not record the review', description: r.error?.message, variant: 'destructive' });
        return;
      }
      setComments((c) => ({ ...c, [vars.id]: '' }));
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({
        title:
          vars.outcome === 'approved' ? 'Approved'
          : vars.outcome === 'revision' ? 'Returned for revision'
          : 'Rejected',
      });
    },
  });

  const items = (data?.data ?? []) as ReviewItem[];

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-serif font-semibold tracking-tight">Review Queue</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Work submitted for your review. Nothing here has reached a client yet.
        </p>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Nothing waiting for review.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((it) => {
            const over = it.estimated_hours != null && it.actual_hours > it.estimated_hours;
            return (
              <Card key={it.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <CardTitle className="text-base">{it.title}</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        {it.account_name} · {it.service_line_name} · {it.cycle_label}
                        {' · submitted '}{formatDate(it.submitted_at)}
                        {it.submitted_by_name ? ` by ${it.submitted_by_name}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {it.deliverable_count > 0 && (
                        <Badge variant="outline">
                          <Paperclip className="h-3 w-3 mr-1" />{it.deliverable_count}
                        </Badge>
                      )}
                      <Badge variant={over ? 'destructive' : 'secondary'}>
                        {Number(it.actual_hours).toFixed(2)}h
                        {it.estimated_hours != null ? ` of ${it.estimated_hours}h` : ''}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {it.acceptance_criteria && (
                    <Section title="What was asked for">{it.acceptance_criteria}</Section>
                  )}
                  {it.client_notes && (
                    <Section title="Summary for the client">{it.client_notes}</Section>
                  )}
                  {it.internal_notes && (
                    <Section title="Internal notes">{it.internal_notes}</Section>
                  )}

                  <div>
                    <Textarea
                      rows={2}
                      placeholder="Comment — required context if you are returning this for revision"
                      value={comments[it.id] ?? ''}
                      onChange={(e) => setComments((c) => ({ ...c, [it.id]: e.target.value }))}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => decide.mutate({ id: it.id, outcome: 'approved' })}
                      disabled={decide.isPending}
                    >
                      <Check className="h-4 w-4 mr-1.5" />
                      {it.requires_client_approval ? 'Approve → send to client' : 'Approve'}
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => decide.mutate({ id: it.id, outcome: 'revision' })}
                      disabled={decide.isPending}
                    >
                      <RotateCcw className="h-4 w-4 mr-1.5" /> Return for revision
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => decide.mutate({ id: it.id, outcome: 'rejected' })}
                      disabled={decide.isPending}
                    >
                      <X className="h-4 w-4 mr-1.5" /> Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{title}</p>
      {/* Plain text. client_notes is authored by whoever did the work and can
          reach a client, so it is never rendered as markup. */}
      <p className="text-sm whitespace-pre-wrap">{children}</p>
    </div>
  );
}
